import React, { useEffect, useState } from "react";
import { auth, db } from "../../config/firebaseConfig";
import { Link, useLocation } from "react-router-dom";
import InviteMembers from "./tabs/InviteMembers";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import "./ProjectManager.css";

const ProjectManager = () => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path ? "active" : "";
  // 共通状態
  const [projects, setProjects] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [currentProjectID, setCurrentProjectID] = useState(localStorage.getItem("currentProjectID"));
  const [isAuthorized, setIsAuthorized] = useState(false);
  const currentUser = auth.currentUser;

  // 招待関係
  const [newInviteEmail, setNewInviteEmail] = useState("");
  const [pendingInvites, setPendingInvites] = useState([]);

  // 設定関係
  const categories = ["air", "water", "electric", "network"];
  const [settings, setSettings] = useState({
    air: [],
    water: [],
    electric: [],
    network: [],
  });
  const [selectedCategory, setSelectedCategory] = useState("air");
  const [newOption, setNewOption] = useState("");
  const [newType, setNewType] = useState("");
  const [newPhase, setNewPhase] = useState("");
  const [newVoltage, setNewVoltage] = useState("");

  // 初期タブ制御
  const [activeTab, setActiveTab] = useState("general");

  // --------------------------------------------------
  // 初期読み込み
  // --------------------------------------------------
  useEffect(() => {
    const fetchProjects = async () => {
      if (!currentUser) return;

      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const assignedProjects = userData.assignedProjects || [];

      const q = collection(db, "projects");
      const snapshot = await getDocs(q);
      const filtered = snapshot.docs
        .filter((doc) => assignedProjects.includes(doc.id))
        .map((doc) => ({ id: doc.id, ...doc.data() }));

      setProjects(filtered);

      if (currentProjectID) {
        const projSnap = await getDoc(doc(db, "projects", currentProjectID));
        const projData = projSnap.data();
        if (currentUser.uid === projData.createdBy || userData.role === "admin") {
          setIsAuthorized(true);
          setPendingInvites(projData.pendingInvites || []);
        }
        fetchAllSettings(currentProjectID);
      }
    };

    fetchProjects();
  }, [currentUser, currentProjectID]);

  // --------------------------------------------------
  // 各カテゴリ設定の取得
  // --------------------------------------------------
  const fetchAllSettings = async (projectID) => {
    const newSettings = {};
    for (const cat of categories) {
      const snap = await getDocs(collection(db, `projects/${projectID}/settings/${cat}/${cat}TypeOptions`));
      newSettings[cat] = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    }
    setSettings(newSettings);
  };

  // --------------------------------------------------
  // 一般設定タブ：プロジェクト作成・削除
  // --------------------------------------------------
  const handleCreateProject = async () => {
    if (!projectName.trim()) return;

    const newID = uuidv4();
    const newProject = {
      name: projectName.trim(),
      description: description.trim(),
      createdAt: serverTimestamp(),
      createdBy: currentUser.uid,
      members: [currentUser.uid],
      pendingInvites: [],
      isActive: true,
      roles: { [currentUser.uid]: "admin" },
    };

    await setDoc(doc(db, "projects", newID), newProject);
    await updateDoc(doc(db, "users", currentUser.uid), {
      assignedProjects: arrayUnion(newID),
    });

    setProjects([...projects, { id: newID, ...newProject }]);
    setProjectName("");
    setDescription("");
    alert("プロジェクトを作成しました。");
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm("本当に削除しますか？")) return;
    await deleteDoc(doc(db, "projects", id));
    await updateDoc(doc(db, "users", currentUser.uid), { assignedProjects: arrayRemove(id) });
    setProjects(projects.filter((p) => p.id !== id));
  };

  // // --------------------------------------------------
  // // メンバー招待タブ
  // // --------------------------------------------------
  // const handleInvite = async () => {
  //   if (!newInviteEmail.trim() || !currentProjectID) return;
  //   await updateDoc(doc(db, "projects", currentProjectID), {
  //     pendingInvites: arrayUnion(newInviteEmail),
  //   });
  //   setPendingInvites([...pendingInvites, newInviteEmail]);
  //   setNewInviteEmail("");
  // };

  // const handleRemoveInvite = async (email) => {
  //   await updateDoc(doc(db, "projects", currentProjectID), {
  //     pendingInvites: arrayRemove(email),
  //   });
  //   setPendingInvites(pendingInvites.filter((e) => e !== email));
  // };

  // --------------------------------------------------
  // カテゴリ編集タブ
  // --------------------------------------------------
  const handleAddOption = async (category) => {
    let name = newOption;
    let type = newType || null;

    if (category === "electric") {
      if (!newPhase || !newVoltage) {
        alert("相と電圧を入力してください。");
        return;
      }
      name = `${newPhase}${newVoltage}V`;
    }

    if (["air", "water"].includes(category) && !type) {
      alert("区分（供給／排出）を選択してください。");
      return;
    }

    if (!name) {
      alert("名称を入力してください。");
      return;
    }

    const existing = settings[category]?.some((item) => item.name === name && item.isActive !== false);
    if (existing) {
      alert("同じ名称の項目が既に登録されています。");
      return;
    }

    const newItem = {
      name,
      type,
      phase: newPhase || null,
      voltage: newVoltage ? Number(newVoltage) : null,
      createdBy: currentUser.displayName,
      createdAt: serverTimestamp(),
      isActive: true,
    };

    try {
      const docRef = await addDoc(
        collection(db, `projects/${currentProjectID}/settings/${category}/${category}TypeOptions`),
        newItem
      );

      setSettings((prev) => ({
        ...prev,
        [category]: [...prev[category], { id: docRef.id, ...newItem, createdAt: new Date() }],
      }));

      setNewOption("");
      setNewType("");
      setNewPhase("");
      setNewVoltage("");
    } catch (error) {
      console.error("Error adding option:", error);
      alert("登録に失敗しました。");
    }
  };

  const handleDeleteOption = async (category, id) => {
    if (!window.confirm("この項目を非表示にしますか？")) return;
    try {
      const path = `projects/${currentProjectID}/settings/${category}/${category}TypeOptions`;
      const docRef = doc(db, path, id);
      await updateDoc(docRef, {
        isActive: false,
        deletedAt: new Date(),
        deletedBy: currentUser?.displayName || "unknown",
      });
      setSettings((prev) => ({
        ...prev,
        [category]: prev[category].filter((s) => s.id !== id),
      }));
    } catch (error) {
      console.error("削除エラー:", error);
      alert("削除に失敗しました。");
    }
  };

  // --------------------------------------------------
  // UI（タブ構成）
  // --------------------------------------------------
  return (
    <div className="project-tabs">
      <h1 className="page-title">プロジェクト管理</h1>

      {/* --- タブボタン --- */}
      <div className="tabs">
        <button
          className={`tab__menu-item ${activeTab === "general" ? "is-active" : ""}`}
          onClick={() => setActiveTab("general")}
        >
          プロジェクト作成
        </button>
        <button
          className={`tab__menu-item ${activeTab === "projectEdit" ? "is-active" : ""}`}
          onClick={() => setActiveTab("projectEdit")}
        >
          プロジェクト編集
        </button>
        <button
          className={`tab__menu-item ${activeTab === "invite" ? "is-active" : ""}`}
          onClick={() => setActiveTab("invite")}
        >
          メンバー招待
        </button>
        <button
          className={`tab__menu-item ${activeTab === "categoryEdit" ? "is-active" : ""}`}
          onClick={() => setActiveTab("categoryEdit")}
        >
          カテゴリー編集
        </button>
      </div>

      {/* --- タブ内容 --- */}
      <div className="tab-content">
        {/* 一般設定タブ */}
        {activeTab === "general" && (
          <section className="card-section">
            <h2>プロジェクト作成</h2>
            <div className="form-group">
              <label>プロジェクト名</label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="プロジェクト名を入力"
              />
            </div>
            <div className="form-group">
              <label>説明</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="プロジェクトの概要"
                rows={3}
              />
            </div>
            <div className="form-footer">
              <button className="btn-primary" onClick={handleCreateProject}>
                作成
              </button>
            </div>
          </section>
        )}
        {activeTab === "projectEdit" && (
          <section className="card-section">
            <h2>あなたのプロジェクト一覧</h2>
            <div className="project-list">
              {projects.length === 0 ? (
                <p className="no-data">プロジェクトがありません。</p>
              ) : (
                projects.map((p) => (
                  <div className="project-item" key={p.id}>
                    <div>
                      <h3>{p.name}</h3>
                      <p>{p.description}</p>
                    </div>
                    {isAuthorized && (
                      <button className="btn-danger" onClick={() => handleDeleteProject(p.id)}>
                        削除
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </section>
        )}

        {/* メンバー招待タブ */}
        {activeTab === "invite" && (
          <section className="card-section">
              <InviteMembers />
          </section>
        )}

        {/* 設定編集タブ */}
        {activeTab === "categoryEdit" && currentProjectID && (
          <section className="card-section">
            <h2>設定編集（カテゴリ別）</h2>
            <div className="settings-controls">
              <label>カテゴリ選択：</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="electric">電気</option>
                <option value="network">ネットワーク</option>
                <option value="air">給排気</option>
                <option value="water">給排水</option>
              </select>
            </div>

            {/* 入力フォーム */}
            <div className="option-add">
              {selectedCategory !== "electric" && (
                <input
                  type="text"
                  placeholder="新しい項目名"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                />
              )}

              {selectedCategory === "electric" && (
                <div style={{ display: "flex", gap: "8px" }}>
                  <select value={newPhase} onChange={(e) => setNewPhase(e.target.value)}>
                    <option value="">相を選択</option>
                    <option value="単相">単相</option>
                    <option value="三相">三相</option>
                  </select>
                  <input
                    type="number"
                    placeholder="電圧(V)"
                    value={newVoltage}
                    onChange={(e) => setNewVoltage(e.target.value)}
                  />
                  <input
                    type="text"
                    value={newPhase && newVoltage ? `${newPhase}${newVoltage}V` : ""}
                    readOnly
                    style={{
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #ccc",
                      width: "120px",
                    }}
                  />
                </div>
              )}

              {["air", "water"].includes(selectedCategory) && (
                <select value={newType} onChange={(e) => setNewType(e.target.value)}>
                  <option value="">供給／排出を選択</option>
                  <option value="供給">供給</option>
                  <option value="排出">排出</option>
                </select>
              )}

              <button className="btn-primary" onClick={() => handleAddOption(selectedCategory)}>
                追加
              </button>
            </div>

            {/* テーブル表示 */}
            <table className={`settings-table ${selectedCategory}-theme`}>
              <thead>
                <tr>
                  <th>名称</th>
                  {["air", "water"].includes(selectedCategory) && <th>種別</th>}
                  <th>作成者</th>
                  <th>登録日</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {(settings[selectedCategory] ?? [])
                  .filter((s) => s.isActive !== false)
                  .map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      {["air", "water"].includes(selectedCategory) && <td>{s.type}</td>}
                      <td>{s.createdBy}</td>
                      <td>
                        {s.createdAt?.toDate
                          ? s.createdAt.toDate().toLocaleString("ja-JP")
                          : "-"}
                      </td>
                      <td>
                        {isAuthorized && (
                          <button
                            className="btn-danger"
                            onClick={() => handleDeleteOption(selectedCategory, s.id)}
                          >
                            削除
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProjectManager;
